import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/authContext';
import { 
  createSupportChat, 
  getSupportChat, 
  sendSupportMessage, 
  getSupportMessages, 
  listenToSupportMessages
} from '../../firebase/messages';
import { toast } from 'react-toastify';

const LiveChat = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [chatStatus, setChatStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [unsubscribe, setUnsubscribe] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Periodic refresh to check for new messages
  const startPeriodicRefresh = (chatId) => {
    const interval = setInterval(async () => {
      if (chatId) {
        const updatedMessages = await getSupportMessages(chatId);
        
        if (updatedMessages.length !== messages.length) {
          setMessages(updatedMessages);
        }
      }
    }, 5000); // Check every 5 seconds
    
    setRefreshInterval(interval);
  };

  const stopPeriodicRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      initializeChat();
    }
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      stopPeriodicRefresh(); // Stop periodic refresh
    };
  }, [isOpen, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = async () => {
    try {
      setIsLoading(true);
      
      // Check if user already has any support chat (most recent)
      let existingChat = await getSupportChat(user.uid);
      
      if (existingChat) {
        // User has existing chat, load it
        setChatId(existingChat.id);
        setChatStatus(existingChat.status);
        
        // Load existing messages immediately
        const existingMessages = await getSupportMessages(existingChat.id);
        setMessages(existingMessages);
        
        // Set up real-time listener for new messages
        const unsubscribeListener = listenToSupportMessages(existingChat.id, (newMessages) => {
          setMessages(newMessages);
        });
        
        setUnsubscribe(unsubscribeListener);
        
        if (existingChat.status === 'resolved') {
          toast.info('This chat was previously resolved. You can continue the conversation.');
        }
        startPeriodicRefresh(existingChat.id); // Start periodic refresh for existing chat
      } else {
        // No existing chat - don't create one yet, just show empty state
        setMessages([]);
        setChatStatus(null);
      }
      
    } catch (error) {
      console.error('=== ERROR INITIALIZING CHAT ===');
      console.error('Error details:', error);
      toast.error('Failed to initialize chat. Please try again.');
    } finally {
      setIsLoading(false);
      console.log('=== CHAT INITIALIZATION COMPLETE ===');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      console.log('Cannot send empty message');
      return;
    }

    try {
      setIsTyping(true);
      console.log('=== SENDING MESSAGE ===');
      console.log('Message text:', newMessage.trim());
      
      let currentChatId = chatId;
      console.log('Current chat ID before sending:', currentChatId);
      
      // If no chat exists yet, create one
      if (!currentChatId) {
        console.log('=== CREATING NEW CHAT ===');
        console.log('Creating new support chat for first message...');
        const newChat = await createSupportChat(user);
        currentChatId = newChat.id;
        setChatId(currentChatId);
        setChatStatus('active');
        console.log('New chat created with ID:', currentChatId);
        console.log('New chat data:', newChat);
        
        // Set up real-time listener for the new chat
        console.log('=== SETTING UP LISTENER FOR NEW CHAT ===');
        const unsubscribeListener = listenToSupportMessages(currentChatId, (newMessages) => {
          console.log('=== REAL-TIME UPDATE FOR NEW CHAT ===');
          console.log('New messages from listener:', newMessages);
          setMessages(newMessages);
        });
        setUnsubscribe(unsubscribeListener);
        
        toast.success('Support chat created! Our team will respond shortly.');
        startPeriodicRefresh(currentChatId); // Start periodic refresh for new chat
      }
      
      const messageData = {
        senderId: user.uid,
        senderName: user.displayName || user.email,
        senderType: 'user',
        message: newMessage.trim()
      };
      
      await sendSupportMessage(currentChatId, messageData);
      setNewMessage('');
      setChatStatus('active');
      toast.success('Message sent! Our support team will respond soon.');
      
      // Manual refresh to ensure immediate UI update since real-time listener isn't working
      const updatedMessages = await getSupportMessages(currentChatId);
      setMessages(updatedMessages);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;



  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-[#1a1a1a] border border-gray rounded-lg shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="bg-primary p-4 rounded-t-lg flex justify-between items-center">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
          <h3 className="text-white font-semibold">Live Support</h3>
          {chatStatus && (
            <span className={`ml-2 px-2 py-1 rounded text-xs ${
              chatStatus === 'active' ? 'bg-green-600 text-white' : 
              chatStatus === 'resolved' ? 'bg-blue-600 text-white' : 
              'bg-gray-600 text-white'
            }`}>
              {chatStatus}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-300 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-white">Loading chat...</div>
          </div>
        ) : (
          <>
            {messages.length === 0 ? (
              <div className="text-center text-gray-400 mt-8">
                <div className="text-sm mb-2">Welcome to live support!</div>
                <div className="text-xs">How can we help you today?</div>
                <div className="text-xs mt-2 text-gray-500">
                  Type your message below to start a conversation.
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.senderType === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                      message.senderType === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-gray-700 text-white'
                    }`}
                  >
                    <div className="text-xs opacity-70 mb-1">
                      {message.senderType === 'user' ? 'You' : 'Support Team'}
                    </div>
                    <div>{message.message}</div>
                    <div className="text-xs opacity-50 mt-1">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            )}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-700 text-white px-3 py-2 rounded-lg text-sm">
                  <div className="text-xs opacity-70 mb-1">Support Team</div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray">
        <div className="flex space-x-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            rows="2"
            disabled={isTyping}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isTyping}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveChat; 